/*
 * Developed for G.J. Gardner Homes by Softeq Development Corporation
 * http://www.softeq.com
 */

import {
  FinancialIndexes,
  MasterZoneListItemDto,
  OfficeDto,
  ReportFyaDetailsDto,
  ReportFyaKeyGoalDto,
  ReportFyaThresholdDto,
} from '@gh/builder-shared-data';
import {
  composeSetters,
  createArrayHelper,
  EntityConfig,
  entityValidationSetter,
  entityValidatorByEntityConfig,
  fieldStateByEntityConfig,
  fieldValidatorByEntityConfig,
  NumberTypes,
  setEntityField,
  setFieldError,
  setNewDirtyType,
  setUpdatedDirtyType,
} from '@gh/common-shared';
import { ID, WithDirtyType, WithEntityState, WithVersion } from '@gh/common-shared-data';
import { Enum } from '@gh/core-data';
import { validationError } from '@gh/core-ui';
import { FocusedCell } from '@gh/core-ui-grid';
import { coalesce, isNotNil } from '@gh/core-util';
import { compact, find, flatMap, groupBy, isNil, keyBy, mapValues, omit, sortBy, values } from 'lodash';

export type ReportFyaThresholdGroupType = 'lbl_current_month' | 'lbl_job_profit_ytd' | 'lbl_financials_ytd';

export type ReportFyaThreshold = WithVersion<WithDirtyType<WithEntityState<{
  id: ID;
  uniqueId: ID;
  type: FinancialIndexes;
  index: number;
  goodFrom: number;
  goodFromInclude: boolean;
  goodTo: number;
  goodToInclude: boolean;
  okFrom: number;
  okFromInclude: boolean;
  okTo: number;
  okToInclude: boolean;
  badFrom: number;
  badFromInclude: boolean;
  badTo: number;
  badToInclude: boolean;
  groupType?: ReportFyaThresholdGroupType;
}>>>;
export type ReportFyaKeyGoal = ReportFyaKeyGoalDto;
export type ReportFyaMasterZone = MasterZoneListItemDto;
export type ReportFyaOffice = OfficeDto;
export type ReportFyaKeyGoalFilter = {
  masterZone: ReportFyaMasterZone;
  office: ReportFyaOffice;
};
type ReportFyaThresholdFieldQualityBundle = {
  threshold: ReportFyaThreshold;
  group: { quality: string; field: string };
};

export type ReportFyaDetails = {
  thresholds: ReportFyaThreshold[];
  keyGoals: Hash<ReportFyaKeyGoal>;
  selectedTab: ReportFyaDetailsTab;
  focusedCell?: FocusedCell;
};

export enum ReportFyaDetailsTab { IndexValues = 'INDEX_VALUES', KeyGoals = 'KEY_GOALS' }

export const generateReportFyaThresholdUniqueId = (type: FinancialIndexes, n: number) => `${type}:${n}`;
export const getReportFyaThresholdId = (threshold: ReportFyaThreshold) => threshold.uniqueId;

export type ReportFyaThresholdRange = { quality: string; index: number; sourceFields: string[] };
export type ReportFyaThresholdInterval = {
  quality: string;
  index: number;
  fromInclude: boolean;
  toInclude: boolean;
  from: number;
  to: number;
  sourceFields: string[];
};

const REPORT_FYA_THRESHOLD_RANGE_DEFS = [
  { quality: 'bad', fromField: 'badFrom', toField: 'badTo' },
  { quality: 'ok', fromField: 'okFrom', toField: 'okTo' },
  { quality: 'good', fromField: 'goodFrom', toField: 'goodTo' },
];
const REPORT_FYA_THRESHOLD_FIELD_QUALITY_LEVEL = {
  'badFrom': 'bad',
  'badTo': 'bad',
  'okFrom': 'ok',
  'okTo': 'ok',
  'goodFrom': 'good',
  'goodTo': 'good',
};
const REPORT_FYA_THRESHOLD_VALUE_FIELD_ORDER = {
  'badFrom': 1,
  'badTo': 2,
  'okFrom': 3,
  'okTo': 4,
  'goodFrom': 5,
  'goodTo': 6,
};
export const getReportFyaThresholdValueFieldOrder = (field: string) => REPORT_FYA_THRESHOLD_VALUE_FIELD_ORDER[field];

export const reportFyaThresholdArr = createArrayHelper(getReportFyaThresholdId);

export const isReportFyaThresholdGroup = (threshold: ReportFyaThreshold) => isNotNil(threshold.groupType);
export const isReportFyaThresholdRegular = (threshold: ReportFyaThreshold) => !isReportFyaThresholdGroup(threshold);

const createThresholdGroup = (groupType: ReportFyaThresholdGroupType): ReportFyaThreshold => <any>({
  uniqueId: groupType,
  groupType,
});

const mergeThresholdGroups = (indices: ReportFyaThreshold[]) =>
  indices.reduce(
    (groupedThresholds, threshold) => {
      if (threshold.index === 1) {
        switch (threshold.type) {
          case FinancialIndexes.Sales:
            groupedThresholds.push(createThresholdGroup('lbl_current_month'));
            break;
          case FinancialIndexes.FinalizedJobGp:
            groupedThresholds.push(createThresholdGroup('lbl_job_profit_ytd'));
            break;
          case FinancialIndexes.SalesYtd:
            groupedThresholds.push(createThresholdGroup('lbl_financials_ytd'));
            break;
          default:
            break;
        }
      }
      groupedThresholds.push(threshold);
      return groupedThresholds;
    },
    <ReportFyaThreshold[]>[]);

export const REPORT_FYA_DETAILS_INIT: ReportFyaDetails = {
  thresholds: [],
  keyGoals: {},
  selectedTab: ReportFyaDetailsTab.IndexValues,
};

export const getReportFyaNumberType = ({ type }: ReportFyaThreshold) => {
  switch (type) {
    case FinancialIndexes.Sales:
    case FinancialIndexes.Overheads:
    case FinancialIndexes.AdvertisingSpend:
    case FinancialIndexes.Debtors:
    case FinancialIndexes.SalesYtd:
    case FinancialIndexes.Breakeven:
    case FinancialIndexes.AverageFinalizedGrossProfit:
      return NumberTypes.SignedPrice;
    case FinancialIndexes.ProfitOnFinalizedJobs:
    case FinancialIndexes.GpSales:
    case FinancialIndexes.NpSales:
    case FinancialIndexes.OverheadsSales:
    case FinancialIndexes.WagesSales:
    case FinancialIndexes.AdvertisingSales:
    case FinancialIndexes.FinalizedJobGp:
    case FinancialIndexes.FinalizedJobNp:
    case FinancialIndexes.ContractsProfitForecastNp:
    case FinancialIndexes.GpSales2:
    case FinancialIndexes.OverheadsSales2:
    case FinancialIndexes.AdvertisingSales2:
    case FinancialIndexes.WagesSales2:
    case FinancialIndexes.NpSales2:
      return NumberTypes.Percents;
    default:
      throw new Error(`Unknown threshold type: ${type}`);
  }
};

const REPORT_FYA_THRESHOLD_ENTITY_CONFIG: EntityConfig<ReportFyaThreshold> = {
  goodFrom: { editable: isReportFyaThresholdRegular, type: getReportFyaNumberType },
  goodFromInclude: { editable: isReportFyaThresholdRegular },
  goodTo: { editable: isReportFyaThresholdRegular, type: getReportFyaNumberType },
  goodToInclude: { editable: isReportFyaThresholdRegular },
  okFrom: { editable: isReportFyaThresholdRegular, type: getReportFyaNumberType },
  okFromInclude: { editable: isReportFyaThresholdRegular },
  okTo: { editable: isReportFyaThresholdRegular, type: getReportFyaNumberType },
  okToInclude: { editable: isReportFyaThresholdRegular },
  badFrom: { editable: isReportFyaThresholdRegular, type: getReportFyaNumberType },
  badFromInclude: { editable: isReportFyaThresholdRegular },
  badTo: { editable: isReportFyaThresholdRegular, type: getReportFyaNumberType },
  badToInclude: { editable: isReportFyaThresholdRegular },
};

export const validateReportFyaThreshold = entityValidatorByEntityConfig(REPORT_FYA_THRESHOLD_ENTITY_CONFIG);
export const validateReportFyaThresholdField = fieldValidatorByEntityConfig(REPORT_FYA_THRESHOLD_ENTITY_CONFIG);
export const reportFyaThresholdFieldState = fieldStateByEntityConfig(REPORT_FYA_THRESHOLD_ENTITY_CONFIG);
export const setReportFyaThresholdField = composeSetters(
  setEntityField,
  // if threshold exists => set UPDATED dirty type, otherwise => it is a new entity
  (threshold: ReportFyaThreshold) => threshold.id ? setUpdatedDirtyType(threshold) : setNewDirtyType(threshold),
  entityValidationSetter(validateReportFyaThreshold));

/**
 * Checks that thresholds does not have overlaps in the thresholds of the given type
 * @param thresholds
 * @param type
 */
export const validateReportFyaThresholdOverlaps = (thresholds: ReportFyaThreshold[], type: FinancialIndexes) => {
  const threshold1Id = generateReportFyaThresholdUniqueId(type, 1);
  const threshold2Id = generateReportFyaThresholdUniqueId(type, 2); // tslint:disable-line:no-magic-numbers

  // find overlapped ranges
  const overlappedRanges = calculateThresholdOverlappedRanges(thresholds, type);

  const thresholdIds = [threshold1Id, threshold2Id];
  // update validation state of each threshold
  return reportFyaThresholdArr.updateAll(
    thresholds,
    (threshold) =>
      thresholdIds.includes(getReportFyaThresholdId(threshold)) ?
        REPORT_FYA_THRESHOLD_RANGE_DEFS.reduce(
          (thresholdAcc, { quality, fromField, toField }) =>
            validateThresholdField(
              overlappedRanges,
              validateThresholdField(overlappedRanges, thresholdAcc, fromField),
              toField),
          threshold)
        : threshold);
};

const validateThresholdField = (overlappedRanges: ReportFyaThresholdRange[],
                                threshold: ReportFyaThreshold,
                                field: string) =>
  setFieldError(
    threshold,
    field,
    'overlap',
    hasThresholdRangeFor(overlappedRanges, threshold, field) ? validationError('msg_index_values_overlap') : void 0);

export const calculateThresholdOverlappedRanges = (thresholds: ReportFyaThreshold[],
                                                   type: FinancialIndexes): ReportFyaThresholdRange[] => {
  const threshold1Id = generateReportFyaThresholdUniqueId(type, 1);
  const threshold2Id = generateReportFyaThresholdUniqueId(type, 2); // tslint:disable-line:no-magic-numbers
  // take both thresholds for the given type
  const thresholdsOfType = compact([
    reportFyaThresholdArr.find(thresholds, threshold1Id),
    reportFyaThresholdArr.find(thresholds, threshold2Id),
  ]);
  // build all intervals
  const intervals = flatMap(
    thresholdsOfType,
    (threshold) => flatMap(REPORT_FYA_THRESHOLD_RANGE_DEFS, ({ quality, fromField, toField }) => {
      const from = threshold[fromField];
      const to = threshold[toField];
      if (isNil(from)) {
        if (isNil(to)) {
          return [];
        } else {
          return [buildThresholdInterval(threshold, quality, void 0, toField)];
        }
      } else if (isNil(to)) {
        return [buildThresholdInterval(threshold, quality, fromField, void 0)];
      } else if (from <= to) {
        return [buildThresholdInterval(threshold, quality, fromField, toField)];
      } else {
        return [
          buildThresholdInterval(threshold, quality, fromField, void 0),
          buildThresholdInterval(threshold, quality, void 0, toField),
        ];
      }
    }));
  // take all non-null intervals
  const nonNullIntervals = intervals.filter(({ from, to }) => isNotNil(from) || isNotNil(to));
  // normalize intervals (get rid of nil values)
  const normalizedIntervals = nonNullIntervals.map((threshold) => ({
    ...threshold,
    from: coalesce(threshold.from, Number.NEGATIVE_INFINITY),
    to: coalesce(threshold.to, Number.POSITIVE_INFINITY),
  }));
  // check intersection between intervals of different quality
  const invalidIntervals = normalizedIntervals.filter((sourceInterval) => normalizedIntervals.some((targetInterval) => {
    if (sourceInterval.quality === targetInterval.quality) {
      return false;
    }
    return isThresholdIntervalIntersect(sourceInterval, targetInterval);
  }));
  return invalidIntervals.map(({ quality, index, sourceFields }) => ({ quality, index, sourceFields }));
  // return only intersected ranges
};

const getThresholdFieldQualityLevel = (field: string) => REPORT_FYA_THRESHOLD_FIELD_QUALITY_LEVEL[field];

const buildThresholdInterval = (threshold: ReportFyaThreshold,
                                quality: string,
                                fromField?: string,
                                toField?: string): ReportFyaThresholdInterval => ({
  fromInclude: fromField ? getReportFyaThresholdIncludeField(threshold, fromField) : true,
  from: fromField ? threshold[fromField] : Number.NEGATIVE_INFINITY,
  toInclude: toField ? getReportFyaThresholdIncludeField(threshold, toField) : true,
  to: toField ? threshold[toField] : Number.POSITIVE_INFINITY,
  quality,
  index: threshold.index,
  sourceFields: compact([fromField, toField]),
});

export const hasThresholdRangeFor = (ranges: ReportFyaThresholdRange[],
                                     threshold: ReportFyaThreshold,
                                     field: string) => {
  const quality = getThresholdFieldQualityLevel(field);
  return isNotNil(find(
    ranges,
    (range) => range.quality === quality && range.index === threshold.index && range.sourceFields.includes(field)));
};

const isThresholdIntervalIntersect = (source: ReportFyaThresholdInterval, target: ReportFyaThresholdInterval) => {
  // first interval is placed outside of the second interval
  if (source.to < target.from || source.from > target.to) {
    return false;
  }
  // one of borders is included in both intervals
  if (source.to === target.from) {
    return source.toInclude ? target.fromInclude : false;
  } else if (source.from === target.to) {
    return source.fromInclude ? target.toInclude : false;
  }
  return true;
};

const getReportFyaThresholdIncludeField = (threshold: ReportFyaThreshold, field: string): boolean =>
  threshold[`${field}Include`];

/**
 * Check that ALL values in one quality group have the same value of include flag
 * @param group
 */
const isQualityGroupHasSameIncludeFlag = (group: ReportFyaThresholdFieldQualityBundle[]) =>
  group.every(({ group: { field }, threshold }, _, [{ group: { field: firstField }, threshold: firstThreshold }]) =>
    // tslint:disable-next-line:max-line-length
    getReportFyaThresholdIncludeField(threshold, field) === getReportFyaThresholdIncludeField(firstThreshold, firstField));

const isQualityGroupBoundaryOverlaps = (group1: ReportFyaThresholdFieldQualityBundle[],
                                        group2: ReportFyaThresholdFieldQualityBundle[]) => {
  // check that each separate group has the same value of include flag
  if (!isQualityGroupHasSameIncludeFlag(group1) || !isQualityGroupHasSameIncludeFlag(group2)) {
    return true;
  }

  // if merged group have the same include flag => groups are overlapped
  return isQualityGroupHasSameIncludeFlag(group1.concat(group2));
};

export const emptyReportFyaThresholdDto = (type: FinancialIndexes): ReportFyaThresholdDto => <any>({
  type,
  goodFrom1Include: false,
  goodTo1Include: false,
  okFrom1Include: false,
  okTo1Include: false,
  badFrom1Include: false,
  badTo1Include: false,
  goodFrom2Include: false,
  goodTo2Include: false,
  okFrom2Include: false,
  okTo2Include: false,
  badFrom2Include: false,
  badTo2Include: false,
});

/**
 * Guarantee that list of thresholds complete: it should have entries for ALL FINACIAL_INDEXES
 * @param thresholds
 */
export const completeReportFyaThresholdDtoSet = (thresholds: ReportFyaThresholdDto[]): ReportFyaThresholdDto[] => {
  const thresholdIndex = keyBy(thresholds, 'type');
  return Enum.names(FinancialIndexes)
    .map((type) => thresholdIndex[type] || emptyReportFyaThresholdDto(<FinancialIndexes>type));
};

export const reportFyaThresholdFromDto = (dto: ReportFyaThresholdDto, n: number): ReportFyaThreshold => ({
  uniqueId: generateReportFyaThresholdUniqueId(dto.type, n),
  id: dto.id,
  version: dto.version,
  type: dto.type,
  index: n,
  goodFrom: n === 1 ? dto.goodFrom1 : dto.goodFrom2,
  goodFromInclude: n === 1 ? dto.goodFrom1Include : dto.goodFrom2Include,
  goodTo: n === 1 ? dto.goodTo1 : dto.goodTo2,
  goodToInclude: n === 1 ? dto.goodTo1Include : dto.goodTo2Include,
  okFrom: n === 1 ? dto.okFrom1 : dto.okFrom2,
  okFromInclude: n === 1 ? dto.okFrom1Include : dto.okFrom2Include,
  okTo: n === 1 ? dto.okTo1 : dto.okTo2,
  okToInclude: n === 1 ? dto.okTo1Include : dto.okTo2Include,
  badFrom: n === 1 ? dto.badFrom1 : dto.badFrom2,
  badFromInclude: n === 1 ? dto.badFrom1Include : dto.badFrom2Include,
  badTo: n === 1 ? dto.badTo1 : dto.badTo2,
  badToInclude: n === 1 ? dto.badTo1Include : dto.badTo2Include,
});

export const reportFyaThresholdToDto = (details1: ReportFyaThreshold,
                                        details2: ReportFyaThreshold): ReportFyaThresholdDto => ({
  id: details1.id,
  version: details1.version,
  type: details1.type,
  goodFrom1: details1.goodFrom,
  goodFrom1Include: details1.goodFromInclude,
  goodTo1: details1.goodTo,
  goodTo1Include: details1.goodToInclude,
  okFrom1: details1.okFrom,
  okFrom1Include: details1.okFromInclude,
  okTo1: details1.okTo,
  okTo1Include: details1.okToInclude,
  badFrom1: details1.badFrom,
  badFrom1Include: details1.badFromInclude,
  badTo1: details1.badTo,
  badTo1Include: details1.badToInclude,
  goodFrom2: details2.goodFrom,
  goodFrom2Include: details2.goodFromInclude,
  goodTo2: details2.goodTo,
  goodTo2Include: details2.goodToInclude,
  okFrom2: details2.okFrom,
  okFrom2Include: details2.okFromInclude,
  okTo2: details2.okTo,
  okTo2Include: details2.okToInclude,
  badFrom2: details2.badFrom,
  badFrom2Include: details2.badFromInclude,
  badTo2: details2.badTo,
  badTo2Include: details2.badToInclude,
  dirtyType: details1.dirtyType || details2.dirtyType,
});

export const reportFyaDetailsFromDto = (dto: ReportFyaDetailsDto): ReportFyaDetails => ({
  thresholds: mergeThresholdGroups(flatMap(completeReportFyaThresholdDtoSet(dto.thresholds), (threshold) => [
    reportFyaThresholdFromDto(threshold, 1),
    reportFyaThresholdFromDto(threshold, 2), // tslint:disable-line:no-magic-numbers
  ])),
  keyGoals: mapValues(keyBy(dto.keyGoals, 'officeId'), reportFyaKeyGoalFromDto),
  selectedTab: ReportFyaDetailsTab.IndexValues,
});
export const reportFyaDetailsToSaveDto = (details: ReportFyaDetails): ReportFyaDetailsDto => ({
  thresholds: values(
    groupBy(
      details.thresholds
        .filter(isReportFyaThresholdRegular)
        .map((threshold) => <ReportFyaThreshold>omit(threshold, 'entityState')),
      'type'))
    .map((group) => sortBy(group, 'index'))
    .map(([details1, details2]) => reportFyaThresholdToDto(details1, details2)),
  keyGoals: values(details.keyGoals).map(reportFyaKeyGoalToDto),
});

export const reportFyaKeyGoalFromDto = (dto: ReportFyaKeyGoalDto): ReportFyaKeyGoal => ({
  ...dto,
  keyGoals: fromReportFontSize(dto.keyGoals),
  comments: fromReportFontSize(dto.comments),
});

export const reportFyaKeyGoalToDto = (model: ReportFyaKeyGoal): ReportFyaKeyGoalDto => ({
  ...model,
  keyGoals: toReportFontSize(model.keyGoals),
  comments: toReportFontSize(model.comments),
});

export const replaceFontSize = (text: string, transformFontSize: IdentityFn<number>) => {
  if (text) {
    return text.replace(/(<[^>]+)(font-size:\s*)(\d+(?:\.\d*)?)(px[^>]*>)/g, (_, before, fontSizeSymbol, n, after) =>
      `${before}${fontSizeSymbol}${transformFontSize(Number(n))}${after}`);
  } else {
    return text;
  }
};

// We transform font size using 1.3 ratio
// This is necessary, because Jasper Reports display HTML content with font size multiplied on 1.3
const REPORT_FONT_SIZE_RATIO = 1.3;
export const toReportFontSize = (text: string) =>
  replaceFontSize(text, (n) => n / REPORT_FONT_SIZE_RATIO);
export const fromReportFontSize = (text: string) =>
  replaceFontSize(text, (n) => Math.round(n * REPORT_FONT_SIZE_RATIO));
